import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import DashboardNav from "../components/DashboardNav";
import CampaignCard from "../components/CampaignCard";
import {
  ArrowLeft,
  Building2,
  Phone,
  MapPin,
  FileText,
  Mail,
  ShieldCheck,
  AlertCircle,
  Gamepad2,
  Eye,
  Clock,
  CheckCircle2,
  Pause,
  XCircle,
} from "lucide-react";

function AdminPublisherDetailPage() {
  const { publisherId } = useParams();
  const navigate = useNavigate();

  const [publisher, setPublisher] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const userDoc = await getDoc(doc(db, "users", publisherId));
        if (!userDoc.exists()) {
          setLoading(false);
          return;
        }
        setPublisher({ id: userDoc.id, ...userDoc.data() });

        const campaignSnap = await getDocs(
          query(
            collection(db, "campaigns"),
            where("publisherId", "==", publisherId)
          )
        );
        setCampaigns(
          campaignSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
              const aTime = a.createdAt?.seconds || 0;
              const bTime = b.createdAt?.seconds || 0;
              return bTime - aTime;
            })
        );
      } catch (err) {
        console.error("Failed to fetch publisher:", err);
      }
      setLoading(false);
    }
    fetchData();
  }, [publisherId]);

  function statusIcon(status) {
    switch (status) {
      case "active":
        return <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />;
      case "pending":
        return <Clock className="w-3.5 h-3.5 text-yellow-400" />;
      case "paused":
        return <Pause className="w-3.5 h-3.5 text-gray-400" />;
      case "rejected":
        return <XCircle className="w-3.5 h-3.5 text-red-400" />;
      default:
        return null;
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <DashboardNav />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!publisher) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <DashboardNav />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
          <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Publisher Not Found</h2>
          <button
            onClick={() => navigate("/admin")}
            className="text-brand-400 hover:text-brand-300 text-sm mt-2"
          >
            Back to Admin Panel
          </button>
        </div>
      </div>
    );
  }

  const profile = publisher.publisherProfile || {};
  const totalSpent = campaigns.reduce((sum, c) => sum + (c.totalSpent || 0), 0);
  const totalBudget = campaigns.reduce((sum, c) => sum + (c.maxBudget || 0), 0);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <DashboardNav />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => navigate("/admin")}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Admin Panel
        </button>

        {/* Publisher Header */}
        <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 font-bold text-xl flex-shrink-0">
              {(publisher.displayName || publisher.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold">{publisher.displayName || "Unnamed"}</h1>
              <p className="text-gray-400 text-sm">{publisher.email}</p>
              {profile.companyName && (
                <p className="text-purple-400 text-sm font-medium mt-1">{profile.companyName}</p>
              )}
              <div className="flex flex-wrap gap-3 mt-3">
                <span className="text-xs bg-purple-500/10 text-purple-400 border border-purple-500/20 px-2.5 py-1 rounded-full">
                  Publisher
                </span>
                <span className="text-xs bg-gray-800 text-gray-400 px-2.5 py-1 rounded-full">
                  {campaigns.length} campaign{campaigns.length !== 1 ? "s" : ""}
                </span>
                <span className="text-xs bg-gray-800 text-gray-400 px-2.5 py-1 rounded-full">
                  ${totalSpent.toLocaleString()} spent
                </span>
                <span className="text-xs bg-gray-800 text-gray-400 px-2.5 py-1 rounded-full">
                  ${totalBudget.toLocaleString()} total budget
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Profile Details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Email */}
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Email</p>
                <p className="text-sm text-gray-200">{publisher.email}</p>
                {/* {publisher.emailVerified ? (
                  <span className="inline-flex items-center gap-1 text-xs text-green-400 mt-1">
                    <ShieldCheck className="w-3 h-3" /> Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-yellow-400 mt-1">
                    <AlertCircle className="w-3 h-3" /> Not verified
                  </span>
                )} */}
              </div>
            </div>

            {/* Company Name */}
            <div className="flex items-start gap-3">
              <Building2 className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Company Name</p>
                <p className="text-sm text-gray-200">{profile.companyName || "—"}</p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Phone Number</p>
                <p className="text-sm text-gray-200">{profile.phoneNumber || "—"}</p>
              </div>
            </div>

            {/* Address */}
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Address</p>
                <p className="text-sm text-gray-200">
                  {[profile.address, profile.city, profile.postalCode, profile.country]
                    .filter(Boolean)
                    .join(", ") || "—"}
                </p>
              </div>
            </div>

            {/* Tax ID */}
            <div className="flex items-start gap-3">
              <FileText className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Tax ID / VAT</p>
                <p className="text-sm text-gray-200">{profile.taxId || "—"}</p>
              </div>
            </div>

            {/* Tax Office */}
            <div className="flex items-start gap-3">
              <FileText className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Tax Office</p>
                <p className="text-sm text-gray-200">{profile.taxOffice || "—"}</p>
              </div>
            </div>

            {/* Profile Status */}
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Profile Status</p>
                {profile.profileCompleted ? (
                  <span className="inline-flex items-center gap-1 text-xs text-green-400">
                    <CheckCircle2 className="w-3 h-3" /> Complete
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-yellow-400">
                    <AlertCircle className="w-3 h-3" /> Incomplete
                  </span>
                )}
              </div>
            </div>

            {/* Joined Date */}
            <div className="flex items-start gap-3">
              <Clock className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Joined</p>
                <p className="text-sm text-gray-200">
                  {publisher.createdAt
                    ? new Date(publisher.createdAt).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Campaigns */}
        <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Gamepad2 className="w-5 h-5 text-brand-400" />
            <h2 className="text-lg font-semibold">Campaigns ({campaigns.length})</h2>
          </div>

          {campaigns.length === 0 ? (
            <div className="text-center py-8">
              <Gamepad2 className="w-10 h-10 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No campaigns created yet.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {campaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  linkTo={`/admin/campaigns/${campaign.id}`}
                  actions={
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 text-xs capitalize px-2.5 py-1 rounded-full border bg-gray-800/50 border-gray-700/50 text-gray-400">
                        {statusIcon(campaign.status)}
                        {campaign.status}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/campaigns/${campaign.id}`);
                        }}
                        className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-xl transition-all font-semibold text-sm"
                      >
                        <Eye className="w-4 h-4" />
                        Review
                      </button>
                    </div>
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPublisherDetailPage;
